import { type Component, For, Show, createSignal } from 'solid-js'
import { t } from 'i18next'
import {
  type ModerationMessage,
  type ModerationActionSeverity,
  ModerationActionType,
  moderationActionConfig,
  moderationActionLabel,
  messageTypeLabel
} from '../domain/models/moderation.model'
import { Skeleton, SkeletonLine, SkeletonCircle } from '../../../components/shared/components/Skeleton'
import Tooltip from '../../../components/shared/components/Tooltip'
import { formatDateToRegular, formatDateToTimeAMPM } from '../../../shared/helpers/date.functions'
import { Icon } from '@iconify-icon/solid'
import UserAvatar from '../../../components/shared/components/UserAvatar'

interface ModerationMessageCardProps {
  message: ModerationMessage
  showActions: boolean
  onAction?: (messageId: string, action: ModerationActionType) => void
}

const severityTextColors: Record<ModerationActionSeverity, string> = {
  success: 'text-green-600',
  warning: 'text-amber-500',
  error: 'text-red-600'
}

const actionButtonConfig: Record<ModerationActionType, { icon: string; colorClass: string }> = {
  [ModerationActionType.MarkReviewed]: { icon: 'heroicons:check-circle', colorClass: 'text-green-600' },
  [ModerationActionType.Ban]: { icon: 'heroicons:shield-exclamation', colorClass: 'text-red-600' },
  [ModerationActionType.Deleted]: { icon: 'heroicons:trash', colorClass: 'text-red-600' }
}

const allActionButtons = [
  ModerationActionType.MarkReviewed,
  ModerationActionType.Ban,
  ModerationActionType.Deleted
] as const

const formatDate = (date: Date): string => `${formatDateToRegular(date)}, ${formatDateToTimeAMPM(date)}`

const ActionBadge: Component<{ action: ModerationActionType }> = (props) => {
  const { icon } = actionButtonConfig[props.action]
  const { severity } = moderationActionConfig[props.action]
  const severityTextColor = severityTextColors[severity]

  return (
    <div class={`py-1 flex items-center gap-1.5 ${severityTextColor}`}>
      <Icon icon={icon} class="text-[1rem]" />
      <span class="text-sm font-normal">{moderationActionLabel(props.action)}</span>
    </div>
  )
}

const ModerationMessageCard: Component<ModerationMessageCardProps> = (props) => {
  const [isExiting, setIsExiting] = createSignal(false)

  const handleAction = (action: ModerationActionType): void => {
    setIsExiting(true)
    setTimeout(() => props.onAction?.(props.message.id, action), 500)
  }

  return (
    <div
      class="group border-b border-gray-200 flex flex-col bg-white overflow-hidden"
      classList={{
        'hover:bg-gray-50': props.showActions,
        'animate-card-exit': isExiting()
      }}
    >
      <div class="px-6 pt-6 pb-3 flex flex-col gap-1">
        <div class="flex items-start gap-4">
          <div class="flex-1 flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-xs font-medium text-gray-500 truncate">{formatDate(props.message.date)}</span>
              <span class="w-1 h-1 bg-gray-400 rounded-full shrink-0" />
              <span class="text-xs font-medium text-gray-500 truncate">{messageTypeLabel(props.message.type)}</span>
            </div>
            <div class="text-base font-medium text-gray-900 truncate">{props.message.title}</div>
          </div>

          {/* Action buttons on hover - Inbox only */}
          <Show when={props.showActions}>
            <div class="pl-4 flex items-center gap-2 shrink-0 opacity-0 scale-95 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto">
              <For each={allActionButtons}>
                {(action) => {
                  const { icon, colorClass } = actionButtonConfig[action]
                  return (
                    <Tooltip content={moderationActionLabel(action)} position="top">
                      <button
                        type="button"
                        onClick={() => handleAction(action)}
                        class={`w-8 h-8 rounded-full shadow-sm ring-1 ring-inset ring-gray-200 bg-white hover:bg-gray-50 ${colorClass} flex items-center justify-center transition-colors`}
                      >
                        <Icon icon={icon} class="text-[1rem]" />
                      </button>
                    </Tooltip>
                  )
                }}
              </For>
            </div>
          </Show>
        </div>

        <p class="text-sm text-gray-500 font-normal">{props.message.body}</p>
      </div>

      <div class="px-6 pb-6 flex items-center gap-2">
        <div class="flex-1 flex items-center gap-4">
          <div class="flex items-center gap-1.5">
            <UserAvatar
              src={props.message.authorAvatarUrl}
              name={props.message.authorName}
              class="w-5 h-5 rounded-full"
            />
            <span class="text-sm font-medium text-gray-700">{props.message.authorName}</span>
          </div>
        </div>

        {/* Action badges and automoderation label */}
        <div class="flex items-center gap-4">
          <Show when={!props.showActions}>
            <Show when={props.message.autoModerated}>
              <div class="py-1 flex items-center gap-1.5 text-primary-600">
                <Icon icon="heroicons:cpu-chip" class="text-[1rem]" />
                <span class="text-sm font-normal">{t('ls_moderation_automoderation')}</span>
              </div>
            </Show>
            <Show
              when={props.message.actions.length > 0}
              fallback={
                <div class="py-1 flex items-center gap-1.5 text-green-600">
                  <Icon icon="heroicons:check-circle" class="text-[1rem]" />
                  <span class="text-sm font-normal">{t('ls_moderation_approved')}</span>
                </div>
              }
            >
              <For each={props.message.actions}>{(action) => <ActionBadge action={action} />}</For>
            </Show>
          </Show>
        </div>
      </div>
    </div>
  )
}

export const SkeletonModerationCard: Component = () => (
  <Skeleton class="border-b border-gray-200 bg-white">
    <div class="px-6 pt-6 pb-3 flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <SkeletonLine class="h-3 w-36" />
        <SkeletonLine class="h-3 w-20" />
      </div>
      <SkeletonLine class="h-5 w-72" />
      <SkeletonLine class="h-4 w-full mt-1" />
      <SkeletonLine class="h-4 w-3/4" />
    </div>
    <div class="px-6 pb-6 flex items-center gap-1.5">
      <SkeletonCircle class="h-5 w-5" />
      <SkeletonLine class="h-3.5 w-24" />
    </div>
  </Skeleton>
)

export default ModerationMessageCard
